import { Slider, styled } from '@mui/material'
/**
 * A bidirectional slider that grows from center (0) to either side
 * Range: -2 to 2 with step size of 1
 * The track fills from center (50%) towards left (negative) or right (positive)
 */
export const BidirectionalSlider = styled(Slider)<{ isAI360Disabled?: boolean }>(({ theme, value, isAI360Disabled }) => {
  const numValue = typeof value === 'number' ? value : 0

  // Determine direction of the track fill
  const isNegative = numValue < 0 // Track should grow left from center

  // Calculate track position as percentage of total width
  const centerPercent = 50 // Center point of the slider
  const range = 4 // Total range: -2 to 2 = 4 units

  // Convert absolute value to percentage of total width
  // Example: value=-2 → offsetPercent=50%, value=1 → offsetPercent=25%
  const offsetPercent = (Math.abs(numValue) / range) * 100

  // Calculate where the track should start (left edge position)
  // For negative values: start left of center (50% - offset)
  // For positive values: start at center (50%)
  // For zero: trackLeft=50%, trackWidth=0 (no visible track)
  const trackLeft = isNegative ? centerPercent - offsetPercent : centerPercent

  // Calculate track width as percentage
  // This is how far the track extends from its starting point
  const trackWidth = offsetPercent

  return {
    /* Slider Container Style */
    marginLeft: 10,
    maxWidth: 'calc(100% - 18px)',
    height: '18px',
    padding: 0,
    '@media (pointer: coarse)': {
      /** mobile view - remove padding for touch devices */
      padding: 0,
    },

    /* Track Styles (The filled portion showing current value) */
    '& .MuiSlider-track': {
      // Hide the default MUI track completely
      display: 'none',
    },

    /* Rail Styles (The background bar that spans the entire slider) */
    '& .MuiSlider-rail': {
      height: '18px',
      border: 'none',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: isAI360Disabled ? theme.palette.grey.grey03 : theme.palette.shades.shade03,
      boxShadow: 'none',
      transform: 'scaleX(1.03)', // Slightly wider than container
      top: 'initial',
      position: 'relative', // Required for absolute positioning of :before element
      opacity: 1,

      /* Custom Bidirectional Track (Overlay on the rail) */
      '&:before': {
        display: isAI360Disabled ? 'none' : 'block',
        content: '""', // Required for pseudo-element to render
        position: 'absolute', // Position relative to the rail

        // Position the track based on calculated values
        left: `${trackLeft}%`, // Start position (center for positive, left of center for negative)
        width: `${trackWidth}%`, // How far to extend from start position

        height: '18px', // Match rail height
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.primary.main,
        top: 0, // Align with top of rail
        // Smooth animation when dragging the slider
        transition: 'left 0.1s, width 0.1s',
        zIndex: 1,
      },
    },

    /* Thumb Styles (The draggable circle) */
    '& .MuiSlider-thumb': {
      display: isAI360Disabled ? 'none' : 'block',
      backgroundColor: theme.palette.grey.shadowLight,
      height: 14,
      width: 14,
    },

    /* Mark Styles (The step indicators on the slider) */
    '& .MuiSlider-mark': {
      height: 18,
      color: theme.palette.shades.shade04, // Color for unmarked steps
      top: 9,
      width: 3,
      borderRadius: 0,
    },

    /* Marked Styles (The active step indicators on the slider) */
    '& .MuiSlider-marked': {
      height: 18,
      color: theme.palette.primary.light, // Color for marked/active steps
      top: 9,
      width: 3,
      borderRadius: 0,
    },
  }
})
